/**
 * URL validation predicates shared between backend DTO and frontend form.
 *
 * RT-R11: defense in depth — backend is authoritative (returns 400 first),
 * frontend gives immediate feedback. The two must produce identical error
 * states for the same input.
 */

const RESERVED_HOSTNAMES = new Set([
  'localhost',
  'ip6-localhost',
  'ip6-loopback',
  'metadata',
  'metadata.google.internal',
  'instance-data',
  'instance-data.ec2.internal',
]);

/**
 * Validate a URL string for the v0.3.0 Game Catalog URL fields.
 *
 * Rules (must match backend `IsNotMetadataCidr` + `@IsUrl`):
 *   - Must parse as a URL.
 *   - Protocol MUST be `https:`.
 *   - Hostname MUST NOT be a reserved hostname.
 *   - Hostname MUST NOT be an IPv4/IPv6 literal in a private/loopback/metadata range.
 */
export function isHttpsUrl(value: string): boolean {
  if (!value) return false;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:') return false;

  const host = url.hostname.toLowerCase();
  const stripped = host.startsWith('[') && host.endsWith(']') ? host.slice(1, -1) : host;
  if (RESERVED_HOSTNAMES.has(stripped)) return false;

  // IPv4 literal check
  const v4 = parseIpv4(stripped);
  if (v4 !== null) {
    if (isInCidr(v4, ipv4FromString('127.0.0.0')!, 8)) return false;
    if (isInCidr(v4, ipv4FromString('10.0.0.0')!, 8)) return false;
    if (isInCidr(v4, ipv4FromString('172.16.0.0')!, 12)) return false;
    if (isInCidr(v4, ipv4FromString('192.168.0.0')!, 16)) return false;
    if (isInCidr(v4, ipv4FromString('169.254.0.0')!, 16)) return false;
    if (isInCidr(v4, ipv4FromString('100.64.0.0')!, 10)) return false;
    return true;
  }

  // IPv6 literal check
  const v6 = parseIpv6(stripped);
  if (v6 !== null) {
    // ::1 loopback
    if (v6.every((g, i) => g === (i === 7 ? 1 : 0))) return false;
    // fc00::/7
    if ((v6[0] & 0xfe00) === 0xfc00) return false;
    // fe80::/10
    if ((v6[0] & 0xffc0) === 0xfe80) return false;
  }

  // Public hostname (not an IP literal, not reserved): allow
  return true;
}

export function httpsUrlErrorMessage(): string {
  return 'Must be a valid HTTPS URL pointing to a public host (no localhost, no private IPs, no metadata services)';
}

function parseIpv4(s: string): number | null {
  const parts = s.split('.');
  if (parts.length !== 4) return null;
  let acc = 0;
  for (const p of parts) {
    if (!/^\d+$/.test(p)) return null;
    const n = Number(p);
    if (n < 0 || n > 255) return null;
    acc = acc * 256 + n;
  }
  return acc >>> 0;
}

function ipv4FromString(s: string): number | null {
  return parseIpv4(s);
}

function isInCidr(addr: number, base: number, prefix: number): boolean {
  if (prefix === 0) return true;
  const mask = prefix === 32 ? 0xffffffff : (~((1 << (32 - prefix)) - 1)) >>> 0;
  return (addr & mask) === (base & mask);
}

function parseIpv6(s: string): number[] | null {
  if (s.length === 0) return null;
  const parts = s.split('::');
  if (parts.length > 2) return null;
  const parseGroup = (g: string): number[] | null => {
    if (g === '') return [];
    const segs = g.split(':');
    if (segs.length > 8) return null;
    const out: number[] = [];
    for (const seg of segs) {
      if (!/^[0-9a-fA-F]{1,4}$/.test(seg)) return null;
      out.push(parseInt(seg, 16));
    }
    return out;
  };
  const head = parseGroup(parts[0]);
  const tail = parts.length === 2 ? parseGroup(parts[1]) : [];
  if (!head || !tail) return null;
  const total = head.length + tail.length;
  if (parts.length === 2 && total >= 8) return null;
  if (total > 8) return null;
  const fill = 8 - total;
  return [...head, ...new Array(fill).fill(0), ...tail];
}